import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';

import { useShop } from '@/hooks/useShop';
import { useInventorySources } from '@/hooks/useInventory';
import { useShopPath } from '@/hooks/useShopPath';
import { useEnsureLoopsContact } from '@/hooks/useEnsureLoopsContact';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { shopNeedsOnboarding, useTrialStatus } from '@/lib/shop-status';
import { describeAssistantPlan } from '@/lib/assistant-plan';

import { AppSidebar } from './AppSidebar';
import { AppSidebarCollapsed } from './AppSidebarCollapsed';
import { AIChatPanel } from './AIChatPanel';
import { LifecycleBanner } from './LifecycleBanner';
import { ExpiredTopBanner } from './ExpiredTopBanner';
import { HealthBannerStack } from './HealthBannerStack';
import { IntroTour } from '@/components/lifecycle/IntroTour';
import { VerifyEmailProvider } from '@/components/verification/VerifyEmailProvider';
import { VerifyEmailBanner } from '@/components/verification/VerifyEmailBanner';
import { LaunchHighlight } from '@/components/lifecycle/LaunchHighlight';
import { ToastProvider } from '@/components/ui/toast';
import { MobileInterstitial } from '@/components/mobile/MobileInterstitial';
import { useMobileNotice } from '@/lib/mobileNotice';
import { useIsTablet } from '@/hooks/use-mobile';
import { AssistantProvider } from '@services/AssistantProvider';
import { DASHBOARD_MAP } from '@/lib/dashboard-map';

/**
 * The main authed shell for the Unify dashboard:
 *
 *   ┌──────────────┬──────────────────┬───────────────┐
 *   │   sidebar    │   page content   │  AI chat      │
 *   └──────────────┴──────────────────┴───────────────┘
 *
 * The shell asks each piece of data it needs directly from the SWR hooks
 * (no shared "lifecycle" abstraction). When a shop still needs onboarding
 * (no GBP, no current POS, no historical ingest) routing redirects the
 * user to `/onboarding`; AppLayout never renders that case itself. A shop
 * that is onboarded but has no channel live yet is handled per-page — the
 * Channels page shows its own first-channel empty state — so there is no
 * app-wide takeover.
 *
 * Trial-expired dimming + the red banner are gated on `useTrialStatus()`
 * which currently returns `null` (Stripe wiring deferred — see
 * `docs/billing-data-requirements.md`).
 */
export function AppLayout() {
	const [chatOpen, setChatOpen] = useState(false);
	const { pathname } = useLocation();
	const navigate = useNavigate();

	const { shop, shops, isLoading: shopLoading } = useShop();
	const { user } = useUser();
	const { shopPath } = useShopPath();
	const { sources, isLoading: sourcesLoading } = useInventorySources(shop?.id);
	const { billing } = useBillingStatus(shop?.id);
	const { plan: assistantPlan, planStatus: assistantPlanStatus } = describeAssistantPlan(billing);
	const trialStatus = useTrialStatus();
	const { shouldShow: showMobileNotice } = useMobileNotice();
	// Auto-collapse the sidebar on tablet / small-desktop widths (<xl) so
	// the main content gets the full viewport. Chat-driven collapse still
	// works on top of this (NEA-6038).
	const isTablet = useIsTablet();
	const sidebarCollapsed = chatOpen || isTablet;

	// NEA-5897 — best-effort once-per-session login-sync so every signed-in user
	// has a fully-populated Loops contact (throttled + impersonation-safe in the hook).
	useEnsureLoopsContact();

	// Settings sits at `/settings` (single-shop) or `/:shopId/settings`
	// (chain). Never run the onboarding takeover on Settings, otherwise a
	// multi-shop user whose first shop still needs setup would get bounced
	// off the Settings page when they try to manage another location.
	const onSettings = /(^|\/)settings(\/|$)/.test(pathname);
	// Multi-shop onboarding renders inside the shell (so the switcher stays
	// reachable) but should fill the area beside the nav — see <main> below.
	const onOnboarding = /(^|\/)onboarding(\/|$)/.test(pathname);
	const needsOnboarding = !onSettings && shopNeedsOnboarding({ shop, sources, sourcesLoading });
	const expired = trialStatus?.expired === true;
	// SWR resolved with no shops (and no dev mock fallback) → push to the
	// dedicated "no shops" page. Mirrors the retailers-dashboard pattern.
	const noShops = !onSettings && !shopLoading && shop === null && shops.length === 0;

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
	}, [pathname]);

	useEffect(() => {
		// Shop-scoped routes prefix with `/:shopId`; single-shop / global stays
		// flat. `shopPath` resolves both, so onboarding lands inside the shell
		// for multi-shop (switcher reachable) and full-screen for single-shop.
		const noShopsPath = shopPath('/no-shops');
		const onboardingPath = shopPath('/onboarding');
		if (noShops && pathname !== noShopsPath) {
			navigate(noShopsPath, { replace: true });
			return;
		}
		if (needsOnboarding && pathname !== onboardingPath) {
			navigate(onboardingPath, { replace: true });
		}
	}, [noShops, needsOnboarding, pathname, navigate, shopPath]);

	return (
		<ToastProvider>
			<VerifyEmailProvider>
				{showMobileNotice ? <MobileInterstitial /> : null}
				<div className="flex min-h-screen w-full flex-col bg-background">
					{expired ? <ExpiredTopBanner /> : null}
					<div className="flex min-h-0 w-full flex-1">
						<div
							className="sticky top-0 h-screen shrink-0 transition-[width] duration-300 ease-in-out"
							style={{ width: sidebarCollapsed ? 60 : 248 }}
						>
							<div className="relative h-full">
								<div
									className={`absolute inset-0 transition-opacity duration-200 ${sidebarCollapsed ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
								>
									<AppSidebar onOpenChat={onOnboarding ? undefined : () => setChatOpen(true)} />
								</div>
								<div
									className={`absolute inset-0 transition-opacity duration-200 ${sidebarCollapsed ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
								>
									<AppSidebarCollapsed onExpand={() => setChatOpen(false)} onOpenChat={() => setChatOpen(true)} />
								</div>
							</div>
						</div>

						{/* Chat column sits between the sidebar and page content (mutually
					    exclusive with the full sidebar — opening it collapses the nav to
					    a 60px rail). Hidden on onboarding, which has its own assistant. */}
						{!onOnboarding && (
							<AssistantProvider
								mode="general"
								currentShop={shop}
								userId={user?.id}
								appMap={DASHBOARD_MAP}
								supportEmail="support@unify.near.st"
								plan={assistantPlan}
								planStatus={assistantPlanStatus}
							>
								<div
									className="sticky top-0 h-screen shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out"
									style={{ width: chatOpen ? 360 : 0 }}
								>
									<div
										className={`h-full transition-opacity duration-200 ${chatOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
									>
										<AIChatPanel onCollapse={() => setChatOpen(false)} isOpen={chatOpen} />
									</div>
								</div>
							</AssistantProvider>
						)}

						<main className="min-w-0 flex-1">
							<VerifyEmailBanner />
							<LifecycleBanner />
							<HealthBannerStack />
							<div className={onOnboarding ? '' : 'mx-auto max-w-[1100px] px-6 py-8 md:px-10 md:py-12'}>
								{/* Settings stays fully interactive when the trial expires —
							    the user needs to be able to add a payment method on the
							    Billing sub-page (and edit Storefront info, manage Team,
							    etc.) to recover. Other pages dim because the shop's
							    customer-facing surfaces are paused. */}
								<div className={expired && !onSettings ? 'pointer-events-none select-none opacity-60' : ''}>
									<Outlet />
								</div>
							</div>
						</main>
					</div>
					<IntroTour />
					<LaunchHighlight />
				</div>
			</VerifyEmailProvider>
		</ToastProvider>
	);
}
